/**
 * Bundled category artwork.
 *
 * Every hero tile needs a logo, but `CatalogApp.logo` is null for all seeded
 * rows, so the rail used to fall back to the hand-drawn pastel `TileMark`
 * placeholders. These are the publishers' real marks, downloaded once by
 * `scripts/fetch_category_logos.py` into `public/categories/` and padded to a
 * uniform 512x512 so wide wordmarks and square app icons read at the same
 * optical size in the grid.
 *
 * `ink` records the artwork's own colour, because the marks are NOT uniform:
 * most are full-colour and need no help, but several are black-on-transparent
 * (invisible on a dark tile) and Standoff 2 is white-on-transparent
 * (invisible on a light tile). The tile inverts those against the active theme
 * so every logo is legible in both modes.
 */
export type CategoryInk = 'auto' | 'dark' | 'light';

export interface CategoryAsset {
  /** Path under `public/`. */
  src: string;
  /**
   * 'auto'  — full-colour or two-tone artwork, never invert. Verified against the
   *            actual SVG fill/stop-colour values in public/categories: e.g.
   *            standoff-2 is full-colour game art, and tiktok is a black+white
   *            two-tone glyph that must flip with the theme, not be judged by
   *            its median luminance.
   * 'dark'  — black ink; invert while the dark theme is active.
   * 'light' — white ink; invert while the light theme is active.
   *
   * This is now only consulted for artwork rendered WITHOUT a plate (an admin
   * upload, or `TileMark`). Bundled marks get a light plate, which is what
   * makes white-ink game logos (brawl-stars, clash-of-clans, playstation)
   * visible at all on the pale winter surface.
   */
  ink: CategoryInk;
}

const BASE = '/categories';

export const CATEGORY_ASSETS: Record<string, CategoryAsset> = {
  accounts: { src: `${BASE}/accounts.svg`, ink: 'auto' },
  'app-store': { src: `${BASE}/app-store.svg`, ink: 'auto' },
  apple: { src: `${BASE}/apple.svg`, ink: 'dark' },
  boosting: { src: `${BASE}/boosting.svg`, ink: 'dark' },
  'brawl-stars': { src: `${BASE}/brawl-stars.svg`, ink: 'auto' },
  chatgpt: { src: `${BASE}/chatgpt.svg`, ink: 'auto' },
  'clash-of-clans': { src: `${BASE}/clash-of-clans.svg`, ink: 'auto' },
  claude: { src: `${BASE}/claude.svg`, ink: 'auto' },
  fortnite: { src: `${BASE}/fortnite.svg`, ink: 'dark' },
  'genshin-impact': { src: `${BASE}/genshin-impact.svg`, ink: 'dark' },
  playstation: { src: `${BASE}/playstation.svg`, ink: 'auto' },
  'pubg-mobile': { src: `${BASE}/pubg-mobile.svg`, ink: 'dark' },
  roblox: { src: `${BASE}/roblox.svg`, ink: 'auto' },
  'standoff-2': { src: `${BASE}/standoff-2.svg`, ink: 'auto' },
  steam: { src: `${BASE}/steam.svg`, ink: 'dark' },
  telegram: { src: `${BASE}/telegram.svg`, ink: 'auto' },
  tiktok: { src: `${BASE}/tiktok.svg`, ink: 'dark' },
};

export function getCategoryAsset(slug: string): CategoryAsset | undefined {
  return CATEGORY_ASSETS[slug];
}

/** True when this artwork must be colour-inverted to stay legible. */
export function shouldInvertAsset(asset: CategoryAsset, isDark: boolean): boolean {
  if (asset.ink === 'dark') return isDark;
  if (asset.ink === 'light') return !isDark;
  return false;
}